import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminTouristSpots,
  saveTouristSpot,
  deleteTouristSpot,
  type TouristSpotPayload,
} from "@/services/adminService";
import type { AdminTouristSpot, AdminSpotStatus } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import MediaPickerModal from "@/pages/portal/admin/components/MediaPickerModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const STATUS_BADGE: Record<AdminSpotStatus, string> = {
  published: "bg-emerald-100 text-emerald-700",
  draft:     "bg-amber-100 text-amber-700",
  archived:  "bg-background-200 text-foreground-600",
};

const EMPTY_FORM: TouristSpotPayload = {
  name: "", slug: "", districtName: "South Canara", category: "", blurb: "",
  description: "", highlights: [], image: "",
  timings: "", entryFee: "", bestTime: "", idealDuration: "", address: "",
  featured: false, status: "draft", sortOrder: 1,
};

const fmt = (n: number) => n.toLocaleString("en-IN");

/**
 * Content a published spot should have before it goes public. A published spot
 * missing any of these gets flagged with a dot in the list so operators can
 * spot gaps at a glance.
 */
function missingContentFields(spot: AdminTouristSpot): string[] {
  const missing: string[] = [];
  if (!spot.description?.trim()) missing.push("description");
  if (!(spot.highlights ?? []).length) missing.push("highlights");
  return missing;
}

export default function TouristSpotsSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminTouristSpots(accessToken),
    [] as AdminTouristSpot[],
    [accessToken],
  );

  const [search, setSearch]   = useState("");
  const [district, setDistrict] = useState("all");
  const [status, setStatus]   = useState<"all" | AdminSpotStatus>("all");
  const [busyId, setBusyId]   = useState<number | null>(null);
  const [form, setForm]       = useState<TouristSpotPayload | null>(null);
  const [editId, setEditId]   = useState<number | null>(null);
  const [highlightDraft, setHighlightDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminTouristSpot | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const districts = useMemo(
    () => Array.from(new Set(data.map((s) => s.districtName))).sort(),
    [data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((s) => {
      if (district !== "all" && s.districtName !== district) return false;
      if (status !== "all" && s.status !== status) return false;
      if (!q) return true;
      return [s.name, s.category, s.districtName, s.address].join(" ").toLowerCase().includes(q);
    });
  }, [data, search, district, status]);

  const published = data.filter((s) => s.status === "published").length;
  const featured  = data.filter((s) => s.featured).length;
  const totalViews = data.reduce((sum, s) => sum + s.views, 0);
  const incompleteCount = data.filter(
    (s) => s.status === "published" && missingContentFields(s).length > 0,
  ).length;

  function openCreate() {
    setForm({ ...EMPTY_FORM, sortOrder: data.length + 1 });
    setHighlightDraft("");
    setEditId(null);
  }

  function openEdit(spot: AdminTouristSpot) {
    setForm({
      name: spot.name, slug: spot.slug, districtName: spot.districtName, category: spot.category,
      blurb: spot.blurb, description: spot.description, highlights: spot.highlights ?? [],
      image: spot.image, timings: spot.timings, entryFee: spot.entryFee,
      bestTime: spot.bestTime, idealDuration: spot.idealDuration, address: spot.address,
      featured: spot.featured, status: spot.status, sortOrder: spot.sortOrder,
    });
    setHighlightDraft("");
    setEditId(spot.id);
  }

  function closeForm() {
    setForm(null);
    setEditId(null);
    setHighlightDraft("");
  }

  function addHighlight() {
    const value = highlightDraft.trim();
    if (!value) return;
    setForm((prev) => {
      if (!prev) return prev;
      const current = prev.highlights ?? [];
      if (current.includes(value)) return prev;
      return { ...prev, highlights: [...current, value] };
    });
    setHighlightDraft("");
  }

  function removeHighlight(index: number) {
    setForm((prev) => {
      if (!prev) return prev;
      return { ...prev, highlights: (prev.highlights ?? []).filter((_, i) => i !== index) };
    });
  }

  function setField<K extends keyof TouristSpotPayload>(key: K, value: TouristSpotPayload[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!form?.name?.trim()) return;
    setBusyId(editId ?? -1);
    try {
      const payload: TouristSpotPayload = {
        ...form,
        slug: (form.slug?.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")),
      };
      await saveTouristSpot(accessToken, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => prev.map((s) => (s.id === editId ? { ...s, ...payload } as AdminTouristSpot : s)));
      } else {
        setData((prev) => [
          ...prev,
          {
            id: Date.now(),
            name: payload.name as string,
            slug: payload.slug as string,
            districtName: payload.districtName ?? "South Canara",
            category: payload.category ?? "",
            blurb: payload.blurb ?? "",
            description: payload.description ?? "",
            image: payload.image ?? "",
            timings: payload.timings ?? "",
            entryFee: payload.entryFee ?? "",
            bestTime: payload.bestTime ?? "",
            idealDuration: payload.idealDuration ?? "",
            address: payload.address ?? "",
            highlights: payload.highlights ?? [],
            featured: !!payload.featured,
            status: (payload.status ?? "draft") as AdminSpotStatus,
            views: 0,
            sortOrder: payload.sortOrder ?? prev.length + 1,
          },
        ]);
      }
      setForm(null);
      setEditId(null);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleFeatured(spot: AdminTouristSpot) {
    const next = !spot.featured;
    setData((prev) => prev.map((s) => (s.id === spot.id ? { ...s, featured: next } : s)));
    setBusyId(spot.id);
    try {
      await saveTouristSpot(accessToken, { featured: next }, spot.id);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleStatus(spot: AdminTouristSpot) {
    const next: AdminSpotStatus = spot.status === "published" ? "draft" : "published";
    setData((prev) => prev.map((s) => (s.id === spot.id ? { ...s, status: next } : s)));
    setBusyId(spot.id);
    try {
      await saveTouristSpot(accessToken, { status: next }, spot.id);
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteTouristSpot(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <AdminStatCard label="Total Spots" value={loading ? "—" : fmt(data.length)} icon="ri-landscape-line" accent="primary" />
        <AdminStatCard label="Published" value={loading ? "—" : fmt(published)} icon="ri-eye-line" accent="emerald" />
        <AdminStatCard label="Featured" value={loading ? "—" : fmt(featured)} icon="ri-star-line" accent="accent" sub="on the home strip" />
        <AdminStatCard label="Spot Views" value={loading ? "—" : fmt(totalViews)} icon="ri-eye-2-line" accent="secondary" />
      </div>

      {!loading && incompleteCount > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500 ring-2 ring-amber-500/20" />
          <span>
            <strong>{incompleteCount}</strong> published {incompleteCount === 1 ? "spot is" : "spots are"} missing a description or highlights.
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search spots, categories…" />
        <select value={district} onChange={(e) => setDistrict(e.target.value)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          <option value="all">All districts</option>
          {districts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 cursor-pointer rounded-lg border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none focus:border-primary-400">
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> Add spot
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${filtered.length} of ${data.length} spots`}
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-landscape-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No tourist spots match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Spot</th>
                  <th className={adminTh}>Category / District</th>
                  <th className={adminTh}>Ideal duration</th>
                  <th className={adminTh}>Views</th>
                  <th className={adminTh}>Status</th>
                  <th className={adminTh}>Featured</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((spot) => (
                  <tr key={spot.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <div className="flex items-center gap-3">
                        <span className="h-9 w-12 shrink-0 overflow-hidden rounded-md bg-background-100">
                          {spot.image ? (
                            <img src={spot.image} alt={spot.name} className="h-full w-full object-top" />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center text-foreground-400">
                              <i className="ri-image-line text-sm" />
                            </span>
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <a href={`/spot/${spot.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground-900 hover:text-primary-700 hover:underline">
                              {spot.name}
                            </a>
                            {spot.status === "published" && missingContentFields(spot).length > 0 && (
                              <button
                                type="button"
                                onClick={() => openEdit(spot)}
                                className="group inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full"
                                title={`Published but missing ${missingContentFields(spot).join(" and ")} — click to complete`}
                                aria-label={`Content incomplete: missing ${missingContentFields(spot).join(" and ")}`}
                              >
                                <span className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-500/20 transition-transform group-hover:scale-125" />
                              </button>
                            )}
                          </div>
                          <p className="truncate text-[11px] text-foreground-400">/spot/{spot.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className={adminTd}>
                      <p className="text-xs text-foreground-700">{spot.category}</p>
                      <p className="text-[11px] text-foreground-400">{spot.districtName}</p>
                    </td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{spot.idealDuration || "—"}</td>
                    <td className={`${adminTd} tabular-nums`}>{fmt(spot.views)}</td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        disabled={busyId === spot.id}
                        onClick={() => toggleStatus(spot)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold capitalize transition-colors ${STATUS_BADGE[spot.status]} disabled:opacity-60`}
                        title={spot.status === "published" ? "Unpublish (move to draft)" : "Publish"}
                      >
                        <i className={spot.status === "published" ? "ri-eye-line text-xs" : "ri-eye-off-line text-xs"} />
                        {spot.status}
                      </button>
                    </td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        disabled={busyId === spot.id}
                        onClick={() => toggleFeatured(spot)}
                        className={adminIconBtn}
                        title={spot.featured ? "Remove from featured" : "Mark as featured"}
                      >
                        <i className={`text-base ${spot.featured ? "ri-star-fill text-accent-500" : "ri-star-line"}`} />
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(spot)} className={adminIconBtn} title="Edit spot">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(spot)} className={adminIconBtn} title="Delete spot">
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-xs text-foreground-500">
        <i className="ri-information-line mr-1" />
        Published spots appear on district pages and the home “Places to visit” strip. Featured spots lead that strip.
        A <span className="mx-0.5 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle ring-2 ring-amber-500/20" /> dot marks published spots missing a description or highlights — click it to complete the content.
      </p>

      {/* Create / edit modal */}
      {form && (
        <AdminModal
          title={editId ? "Edit tourist spot" : "Add tourist spot"}
          subtitle={editId ? form.name : "Create a new place of interest for the directory."}
          onClose={closeForm}
          wide
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeForm} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!form.name?.trim() || busyId !== null} onClick={submit} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create spot"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="spot-name">Name</label>
                <input id="spot-name" type="text" value={form.name ?? ""} onChange={(e) => setField("name", e.target.value)} placeholder="Panambur Beach" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-slug">Slug <span className="ml-1 font-normal normal-case text-foreground-400">(auto if blank)</span></label>
                <input id="spot-slug" type="text" value={form.slug ?? ""} onChange={(e) => setField("slug", e.target.value)} placeholder="panambur-beach" className={adminInput} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={adminLabel} htmlFor="spot-district">District</label>
                <select id="spot-district" value={form.districtName ?? ""} onChange={(e) => setField("districtName", e.target.value)} className={`${adminInput} cursor-pointer`}>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-category">Category</label>
                <input id="spot-category" type="text" value={form.category ?? ""} onChange={(e) => setField("category", e.target.value)} placeholder="Beach" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-order">Sort order</label>
                <input id="spot-order" type="number" min={1} value={form.sortOrder ?? 1} onChange={(e) => setField("sortOrder", Number(e.target.value) || 1)} className={adminInput} />
              </div>
            </div>

            <div>
              <label className={adminLabel} htmlFor="spot-blurb">Short blurb</label>
              <textarea id="spot-blurb" rows={2} maxLength={200} value={form.blurb ?? ""} onChange={(e) => setField("blurb", e.target.value)} className={`${adminInput} h-auto py-2.5`} />
              <p className="mt-1 text-[11px] text-foreground-400">One-line summary shown on cards and list rows (max 200 characters).</p>
            </div>

            <div>
              <label className={adminLabel} htmlFor="spot-description">Full description</label>
              <textarea
                id="spot-description"
                rows={5}
                value={form.description ?? ""}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Tell visitors the story, history and what makes this place worth a trip…"
                className={`${adminInput} h-auto py-2.5 leading-relaxed`}
              />
              <p className="mt-1 text-[11px] text-foreground-400">Shown on the spot page — the long-form content visitors read.</p>
            </div>

            <div>
              <label className={adminLabel} htmlFor="spot-highlight-input">Highlights</label>
              <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                {(form.highlights ?? []).length > 0 && (
                  <ul className="mb-2.5 flex flex-wrap gap-2">
                    {(form.highlights ?? []).map((h, i) => (
                      <li key={`${h}-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 py-1 pl-3 pr-1.5 text-xs text-secondary-900">
                        <span>{h}</span>
                        <button
                          type="button"
                          onClick={() => removeHighlight(i)}
                          className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-secondary-700 transition-colors hover:bg-secondary-200"
                          title={`Remove “${h}”`}
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2">
                  <input
                    id="spot-highlight-input"
                    type="text"
                    value={highlightDraft}
                    onChange={(e) => setHighlightDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHighlight(); } }}
                    placeholder="e.g. Sunset views"
                    className={adminInput}
                  />
                  <button type="button" onClick={addHighlight} disabled={!highlightDraft.trim()} className={adminBtnGhost}>
                    <i className="ri-add-line text-sm" /> Add
                  </button>
                </div>
              </div>
              <p className="mt-1 text-[11px] text-foreground-400">Short bullet points shown as chips on the spot page. Press Enter to add.</p>
            </div>

            <div>
              <label className={adminLabel} htmlFor="spot-address">Address</label>
              <input id="spot-address" type="text" value={form.address ?? ""} onChange={(e) => setField("address", e.target.value)} className={adminInput} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className={adminLabel} htmlFor="spot-timings">Timings</label>
                <input id="spot-timings" type="text" value={form.timings ?? ""} onChange={(e) => setField("timings", e.target.value)} placeholder="Open all day" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-fee">Entry fee</label>
                <input id="spot-fee" type="text" value={form.entryFee ?? ""} onChange={(e) => setField("entryFee", e.target.value)} placeholder="Free entry" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-best">Best time</label>
                <input id="spot-best" type="text" value={form.bestTime ?? ""} onChange={(e) => setField("bestTime", e.target.value)} placeholder="October to February" className={adminInput} />
              </div>
              <div>
                <label className={adminLabel} htmlFor="spot-duration">Ideal duration</label>
                <input id="spot-duration" type="text" value={form.idealDuration ?? ""} onChange={(e) => setField("idealDuration", e.target.value)} placeholder="2 hours" className={adminInput} />
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label className={`${adminLabel} mb-0`} htmlFor="spot-image">Image URL</label>
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className={adminBtnGhost}
                >
                  <i className="ri-image-2-line text-sm" /> Choose from Media Library
                </button>
              </div>
              <input id="spot-image" type="url" value={form.image ?? ""} onChange={(e) => setField("image", e.target.value)} placeholder="https://…" className={adminInput} />
              {form.image ? (
                <span className="mt-2 block h-24 w-40 overflow-hidden rounded-md border border-background-200 bg-background-100">
                  <img src={form.image} alt="Spot preview" className="h-full w-full object-top" />
                </span>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="spot-status">Status</label>
                <select id="spot-status" value={form.status ?? "draft"} onChange={(e) => setField("status", e.target.value as AdminSpotStatus)} className={`${adminInput} cursor-pointer`}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <label className="flex cursor-pointer items-center gap-2.5 self-end pb-2.5 text-sm text-foreground-700">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => setField("featured", e.target.checked)} className="h-4 w-4 cursor-pointer accent-primary-500" />
                Feature on the home “Places to visit” strip
              </label>
            </div>
          </div>
        </AdminModal>
      )}

      {showMediaPicker && (
        <MediaPickerModal
          token={accessToken}
          currentUrl={form?.image}
          onSelect={(url) => setField("image", url)}
          onClose={() => setShowMediaPicker(false)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <AdminModal
          title="Delete tourist spot"
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete spot
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Remove <strong>{deleteTarget.name}</strong> from the directory? Its public page at
            <code className="mx-1 rounded bg-background-100 px-1">/spot/{deleteTarget.slug}</code>
            will no longer be reachable.
          </p>
        </AdminModal>
      )}
    </div>
  );
}